import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline button that uses the browser's SpeechRecognition API to transcribe
 * the user's voice into text. Appends to the existing value via onAppend.
 * Works on Chrome, Edge, Safari, Android Chrome.
 */
export default function DictateButton({ onAppend, label = '', size = 'xs', className = '' }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);
  const interimRef = useRef('');

  useEffect(() => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      return;
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = 'sv-SE';

    r.onresult = (e) => {
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) final += res[0].transcript;
      }
      if (final.trim()) {
        onAppend?.(final.trim());
      }
    };
    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        toast.error('Mikrofon-åtkomst nekad. Aktivera i webbläsarinställningarna.');
      } else if (e.error === 'no-speech') {
        // Silent fail — user just paused
      } else {
        toast.error(`Diktering: ${e.error}`);
      }
      setListening(false);
    };
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => {
      try { r.stop(); } catch { /* ignore */ }
    };
   
  }, []);

  const toggle = () => {
    if (!supported) {
      toast.error('Diktering stöds inte i denna webbläsare');
      return;
    }
    const r = recognitionRef.current;
    if (!r) return;
    if (listening) {
      r.stop();
      setListening(false);
    } else {
      try {
        r.start();
        setListening(true);
        interimRef.current = '';
        toast.success('🎤 Lyssnar… tryck igen för att stoppa', { duration: 2000 });
      } catch {
        // Ignore double-start
      }
    }
  };

  if (!supported) return null;

  const sizeCls = size === 'xs' ? 'p-1 text-[10px]' : 'p-1.5 text-xs';

  return (
    <button
      onClick={toggle}
      className={`neo-btn ${sizeCls} flex items-center gap-1 ${listening ? 'bg-[#FFC8DD] animate-pulse-soft' : 'bg-white'} ${className}`}
      title={listening ? 'Stoppa diktering' : 'Diktera text'}
      data-testid="dictate-button"
      type="button"
    >
      {listening ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
      {label && <span>{listening ? 'Stoppa' : label}</span>}
    </button>
  );
}
